import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

export type AnnotationVisibilitySettings = 'all' | readonly string[];

interface VisibleSectionSettings {
  readonly document: boolean;
  readonly annotations: boolean;
  readonly sealing: boolean;
  readonly other: boolean;
}

export type ControlPanelSection = keyof VisibleSectionSettings;

interface ControlPanelSettings {
  readonly isAvailableTemplatesOpen: boolean;
  readonly visibleSections: VisibleSectionSettings;
}

interface ViewState {
  readonly userAnnotationsVisible: AnnotationVisibilitySettings;
  readonly controlPanelVisibility: ControlPanelSettings;
}

const initialState: ViewState = {
  userAnnotationsVisible: 'all',
  controlPanelVisibility: {
    isAvailableTemplatesOpen: false,
    visibleSections: {
      document: true,
      annotations: true,
      sealing: true,
      other: true,
    },
  },
};

const viewReducer = createSlice({
  name: 'view',
  initialState,
  reducers: {
    viewUserAnnotations: (
      state,
      action: PayloadAction<{ readonly userAnnotationsVisible: AnnotationVisibilitySettings }>,
    ) => {
      const { userAnnotationsVisible } = action.payload;

      state.userAnnotationsVisible = castDraft(userAnnotationsVisible);
    },
    viewAvailableTemplatesOpenToggle: (state) => {
      state.controlPanelVisibility.isAvailableTemplatesOpen = !state.controlPanelVisibility.isAvailableTemplatesOpen;
    },
    viewSectionVisibilityToggle: (state, action: PayloadAction<{ readonly section: ControlPanelSection }>) => {
      const { section } = action.payload;

      state.controlPanelVisibility.visibleSections[section] = !state.controlPanelVisibility.visibleSections[section];
    },
  },
});

export const { viewUserAnnotations, viewAvailableTemplatesOpenToggle, viewSectionVisibilityToggle } =
  viewReducer.actions;
export const view = viewReducer.reducer;
